<template>
  <div class="chat-filters" role="tablist">
    <button
      v-for="option in typeOptions"
      :key="option.value"
      type="button"
      role="tab"
      :aria-selected="typeFilter === option.value"
      class="chat-filters__tab"
      :class="{ 'chat-filters__tab--active': typeFilter === option.value }"
      @click="typeFilter = option.value"
    >
      {{ option.label }}
      <span v-if="counts[option.value]" class="chat-filters__count">{{ counts[option.value] }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { type ChatTypeFilter } from '@/store/chat';

const typeFilter = defineModel<ChatTypeFilter>('typeFilter', { required: true });

const { counts, agent = false } = defineProps<{
  /** Outstanding task count per tab, already narrowed by the search and chips. */
  counts: Record<ChatTypeFilter, number>;
  /** Whether this server has the agent chat: the tab only exists where it does. */
  agent?: boolean;
}>();

// "Others' tasks" reads as "By me": the tasks you assigned to other people.
const typeOptions = computed<{ value: ChatTypeFilter; label: string }[]>(() => [
  { value: 'all', label: 'All' },
  { value: 'messages', label: 'Messages' },
  { value: 'my-tasks', label: 'My tasks' },
  { value: 'others-tasks', label: 'By me' },
  ...(agent ? [{ value: 'agent' as const, label: 'Agent' }] : []),
]);
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.chat-filters {
  display: flex;
  align-items: center;
  gap: 2rem;

  &__tab {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0 0 1.1rem;
    border: none;
    background: none;
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.3rem;
    font-weight: 400;
    color: rgba($c-ink, 0.5);
    transition: color $transition-fast ease;

    &:hover {
      color: $c-ink;
    }

    // Underline sits flush with the header's bottom hairline.
    &::after {
      content: '';
      position: absolute;
      inset: auto 0 -1px 0;
      height: 2px;
      background: $c-ink;
      opacity: 0;
      transition: opacity $transition-fast ease;
    }

    &--active {
      font-weight: 700;
      color: $c-ink;

      &::after {
        opacity: 1;
      }
    }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.8rem;
    height: 1.7rem;
    padding: 0 0.5rem;
    box-sizing: border-box;
    border-radius: $br-pill;
    background: rgba($c-ink, 0.07);
    color: $c-ink-soft;
    font-size: 1.1rem;
    font-weight: 600;
  }

  &__tab--active &__count {
    background: $c-accent;
    color: $c-on-accent;
  }
}
</style>
